import React from 'react';
import tw from 'twin.macro';
import { t } from '@lingui/macro';
import styled from '@emotion/styled';
import { logger } from '$/utils/logger';
import { ApolloError } from '@apollo/client';

import ApolloForm from '$/components/ApolloForm';
import { useSocketContext } from '$/components/Socket/SocketContext';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import { useAttendeePropertiesContext } from '$/components/Property/PropertyProvider';
import { usePropertyFilledLink } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { WebinarPlayerContext, WebinarPlayerUIState } from '$/components/WebinarPlayer/context';

import {
  AttendeeActionPropertiesFragment,
  InteractionDetailsInput,
  InteractionType,
  ReactionForInteractionInput,
  useCreateInteractionReactionMutation,
} from '$/GraphQL/schema/schema';
import { withContextValue } from '$/hooks/withPartialContexts';

import { InteractionInfo } from '../InteractionInfo';
import { StartOrEndMarker } from './StartOrEndMarker';

const EndOfStreamMarker: React.FC = () => <StartOrEndMarker text={t`End of presentation`} />;

interface CardImageStyleComponent {
  floatText?: string;
}

const CardImageStyleComponent = styled.img<CardImageStyleComponent>`
  ${tw`absolute w-full h-20 rounded-lg rounded-b-none`}
  min-height: 120px;
  filter: ${(props): string | undefined => (props.floatText ? 'brightness(0.7)' : undefined)};
`;

type AttendeeCardFormProps = Pick<
  WebinarPlayerUIState,
  | 'ewebinar'
  | 'playbackPositionRef'
  | 'attendeeId'
  | 'rooms'
  | 'mode'
  | 'markInteractionActive'
  | 'markUserActive'
  | 'shortIdRef'
  | 'setWillBeRemovedCard'
  | 'attendee'
> & {
  connectionId: string;
  interaction: InteractionInfo;
  updateProperties: (properties: AttendeeActionPropertiesFragment) => void;
};
export const AttendeeCardForm = withContextValue<
  WebinarPlayerUIState,
  AttendeeCardFormProps,
  Omit<AttendeeCardFormProps, 'interaction'>
>(
  WebinarPlayerContext,
  ({
    ewebinar,
    playbackPositionRef,
    attendeeId,
    rooms,
    mode,
    markInteractionActive,
    markUserActive,
    shortIdRef,
    setWillBeRemovedCard,
    attendee,
  }) => {
    const { updateProperties } = useAttendeePropertiesContext();
    const { connectionId } = useSocketContext();
    return {
      ewebinar,
      playbackPositionRef,
      attendeeId,
      rooms,
      mode,
      markInteractionActive,
      markUserActive,
      shortIdRef,
      updateProperties,
      connectionId,
      setWillBeRemovedCard,
      attendee,
    };
  },
  'AttendeeCardForm'
)(({
  interaction,
  children,
  ewebinar,
  playbackPositionRef,
  attendeeId,
  rooms,
  mode,
  markInteractionActive,
  markUserActive,
  shortIdRef,
  updateProperties,
  connectionId,
  setWillBeRemovedCard,
  attendee,
}) => {
  const [resetAfterSave, setResetAfterSave] = React.useState(false);
  const isEdit = mode === PlayerMode.edit;

  const [createReaction] = useCreateInteractionReactionMutation();
  const onFormChange = React.useCallback(
    (name: string) => {
      logger.debug('ANIM', `I${interaction.id} form change `, name);
      markInteractionActive(interaction);
    },
    [interaction, markInteractionActive]
  );

  const detailLink = usePropertyFilledLink(interaction.details?.link);

  const onFormFocus = React.useCallback(() => {
    logger.debug('ANIM', `I${interaction.id} form focus `);
    markInteractionActive(interaction);
    setResetAfterSave(true);
  }, [interaction, markInteractionActive]);

  const defaultValues = React.useMemo(() => {
    return interaction.attendeeCardDefaultValues(attendee);
  }, [attendee, interaction]);

  const onSubmit = React.useCallback(
    async (data: {
      stringAnswer?: string;
      numberAnswer?: number | string;
      arrayAnswer?: string[];
      details?: InteractionDetailsInput;
    }): Promise<void> => {
      if (detailLink) {
        window.open(detailLink, '_blank');
      }

      if (isEdit || !attendeeId || !interaction.isInteractiveCard()) {
        return;
      }

      const pos = Math.floor(playbackPositionRef.current!);
      const detailsKey = interaction.detailsKey();

      const reaction: ReactionForInteractionInput = {
        interactionId: interaction.id,
        ewebinarId: ewebinar.id,
        interactionType: interaction.type,
        reactionAppearRoom: rooms.getRoomAt(pos).type,
        reactedAt: pos,
        attendeeId: `${attendeeId}`,
        numberAnswer:
          typeof data.numberAnswer === 'string' ? Number(data.numberAnswer) : data.numberAnswer,
        stringAnswer: data.stringAnswer,
        arrayAnswer: data.arrayAnswer,
        detailsFields: detailsKey ? { [detailsKey]: data.details } : {},
      };

      try {
        const res = await createReaction({
          variables: {
            attendeeId,
            data: reaction,
            connectionId: connectionId ?? shortIdRef.current,
            isReplay: mode === PlayerMode.replay,
          },
          update: (_cache, mutationResult) => {
            if (mutationResult.data && mutationResult.data.createInteractionReaction) {
              interaction.id && setWillBeRemovedCard(interaction.id, true);
            }
          },
        });
        markUserActive();
        if (res.data?.createInteractionReaction?.reaction?.properties) {
          updateProperties(res.data?.createInteractionReaction.reaction.properties);
        }
      } catch (e) {
        interaction.id && setWillBeRemovedCard(interaction.id, false);
        const error = e as ApolloError;
        console.error('Error creating reaction ', { error });
        throw error;
      }
    },
    [
      setWillBeRemovedCard,
      attendeeId,
      connectionId,
      createReaction,
      ewebinar.id,
      interaction,
      isEdit,
      markUserActive,
      mode,
      playbackPositionRef,
      rooms,
      shortIdRef,
      updateProperties,
      detailLink,
    ]
  );

  if (interaction.type === InteractionType.EndStream) {
    return <EndOfStreamMarker />;
  }

  if (!interaction.details) return null;

  return (
    <ApolloForm
      onChange={onFormChange}
      onFocus={onFormFocus}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      resetAfterSave={resetAfterSave}
      validationSchema={interaction.attendeeCardValidationRules()}
      shouldUnregister={false}
    >
      {children}
    </ApolloForm>
  );
});
